import './demo-goelett.css'

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'

import { useAuth } from '../../Provider/AuthProvider'
import { FC, invalidateQuery, useCQuery } from '../../Services'
import { cap } from '../../State'
import { translations } from '../../translations'
import { showToast } from '../../utils/toast'
import { CLIENT_CONFIGS, PRESET_STORAGE_KEY } from '../Chat/constants'
import { GigiChatMessages } from '../Gigi/GigiForm/GigiChatMessages'

const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

/**
 * Costruisce lo stato iniziale del form in base al preset selezionato.
 * @param {Object} preset - Configurazione client da CLIENT_CONFIGS
 * @returns {Object} Stato iniziale per il reducer
 */
const buildInitialState = (preset) => ({
  from: preset?.userEmail || '',
  subject: '',
  body: ''
})

const formReducer = (state, action) => {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value }
    case 'RESET':
      return { ...buildInitialState(action.preset) }
    default:
      return state
  }
}

/**
 * Carica i preset salvati dall'utente in localStorage.
 * @returns {Object} Mappa nome → configurazione
 */
const loadSavedPresets = () => {
  try {
    const raw = localStorage.getItem(PRESET_STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

const CUSTOM_PREFIX = 'custom:'

/**
 * Pagina demo semplificata per i flussi email Nora.
 * Mostra un selettore preset (client configs + preset salvati in localStorage),
 * un simulatore di email in arrivo e la conversazione risultante.
 */
export function DemoGoelett() {
  const { user = {} } = useAuth()
  const { user: userObj = {} } = user || {}
  const { id: userId, role, environment } = userObj
  const isOperatorGoelett = role === 'operator' && environment?.codSociety === 'GOE'

  const { data: environments = [], isSuccess } = useCQuery(['environments'])

  const [savedPresets, setSavedPresets] = useState(() => loadSavedPresets())

  const { defaultPreset, defaultKey } = useMemo(() => {
    const goelettPreset = CLIENT_CONFIGS.find(({ id }) => id === 'goelett_test')
    if (isOperatorGoelett) {
      return { defaultPreset: goelettPreset, defaultKey: goelettPreset.id }
    }
    const saved = loadSavedPresets()
    const savedNames = Object.keys(saved)
    if (savedNames.length > 0) {
      const name = savedNames[0]
      const config = saved[name]
      return {
        defaultKey: `${CUSTOM_PREFIX}${name}`,
        defaultPreset: {
          id: `${CUSTOM_PREFIX}${name}`,
          label: name,
          description: t.dgSavedPreset || 'Preset salvato',
          typeEndpointCall: config.callType,
          tomcatCode: config.tomcatCode,
          codSociety: config.codSociety,
          communicationChannel: config.communicationChannel,
          userEmail: config.userEmail,
          processLoadQuotes: config.processLoadQuotes,
          errorProcessToLoad: config.errorProcessToLoad,
          selfManagedDataCollection: config.selfManagedDataCollection,
          dataQuestionUnit: config.dataQuestionUnit,
          unitCallConversationExist: config.unitCallConversationExist,
          escludeMidOfficeFlow: config.escludeMidOfficeFlow,
          goelettRoleEmails: config.goelettRoleEmails
        }
      }
    }
    return { defaultPreset: goelettPreset, defaultKey: goelettPreset.id }
  }, [isOperatorGoelett])

  const [activePreset, setActivePreset] = useState(defaultPreset)
  const [activePresetKey, setActivePresetKey] = useState(defaultKey)
  const [form, dispatch] = useReducer(formReducer, buildInitialState(defaultPreset))
  const [conversationId, setConversationId] = useState()
  const [currentAIC, setCurrentAIC] = useState(null)
  const { data: conversation = {} } = useCQuery(['conversation', conversationId])
  const [messages, setMessages] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [status, setStatus] = useState(null)
  const chatEndRef = useRef(null)

  const env = useMemo(
    () => environments.find(({ codSociety }) => codSociety === activePreset?.codSociety),
    [environments, activePreset]
  )

  useEffect(() => {
    if (!conversation?.messages?.length || conversation.messages.length === messages.length) return
    const sorted = [...conversation.messages].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    )
    setMessages(
      sorted.map(({ message, sender, createdAt }) => ({
        user: sender === 'traveller' ? form.from : 'Nora',
        message,
        createdAt: new Date(createdAt)
      }))
    )
  }, [conversation?.messages])

  useEffect(() => {
    if (!conversationId) return
    const interval = setInterval(() => {
      invalidateQuery(['conversation', conversationId])
    }, 5000)
    return () => clearInterval(interval)
  }, [conversationId])

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const setField = useCallback((field, value) => {
    dispatch({ type: 'SET_FIELD', field, value })
  }, [])

  useEffect(() => {
    const refresh = () => setSavedPresets(loadSavedPresets())
    window.addEventListener('storage', refresh)
    return () => window.removeEventListener('storage', refresh)
  }, [])

  /**
   * Cambia il preset attivo, resetta form e conversazione.
   * Gestisce sia CLIENT_CONFIGS (id diretto) che preset custom (prefisso 'custom:').
   * @param {string} key - ID del preset o 'custom:nome'
   */
  const handlePresetChange = useCallback((key) => {
    let preset
    if (key.startsWith(CUSTOM_PREFIX)) {
      const name = key.slice(CUSTOM_PREFIX.length)
      const saved = loadSavedPresets()
      const config = saved[name]
      if (!config) return
      preset = {
        id: key,
        label: name,
        description: t.dgSavedPreset || 'Preset salvato',
        typeEndpointCall: config.callType,
        tomcatCode: config.tomcatCode,
        codSociety: config.codSociety,
        communicationChannel: config.communicationChannel,
        userEmail: config.userEmail,
        processLoadQuotes: config.processLoadQuotes,
        errorProcessToLoad: config.errorProcessToLoad,
        selfManagedDataCollection: config.selfManagedDataCollection,
        dataQuestionUnit: config.dataQuestionUnit,
        unitCallConversationExist: config.unitCallConversationExist,
        escludeMidOfficeFlow: config.escludeMidOfficeFlow,
        goelettRoleEmails: config.goelettRoleEmails
      }
    } else {
      preset = CLIENT_CONFIGS.find(({ id }) => id === key)
    }
    if (!preset) return
    setActivePreset(preset)
    setActivePresetKey(key)
    dispatch({ type: 'RESET', preset })
    setMessages([])
    setConversationId(undefined)
    setCurrentAIC(null)
    setStatus(null)
  }, [])

  /**
   * Invia la mail simulata all'API /public con il preset attivo.
   * Se esiste una conversazione attiva (currentAIC), la prosegue; altrimenti ne avvia una nuova.
   * Dopo l'invio svuota solo il campo body per permettere follow-up rapidi.
   */
  const handleSend = useCallback(async () => {
    if (!form.body?.trim()) {
      showToast('warn', t.attenzione || 'Attenzione', t.dgMissingBody)
      return
    }

    const aic = currentAIC || `TEST${Date.now().toString(36).toUpperCase()}`
    const subjectLine = form.subject || `Test ${activePreset.label} [AIC:${aic}]`
    const payload = {
      type: activePreset.typeEndpointCall,
      unit: activePreset.typeEndpointCall,
      user: userId,
      pid: env?.defaultPID || '',
      tomcatCode: activePreset.tomcatCode,
      codSociety: activePreset.codSociety,
      userEmail: form.from,
      operatorEmail: form.from,
      userInput: form.body,
      levelFormalita: 75,
      isDebug: false,
      sendEmail: false,
      communicationChannel: activePreset.communicationChannel || 'email',
      mailReferencesEmail: {
        idConversazione: aic,
        emailIsola: form.from,
        subject: subjectLine
      },
      subject: subjectLine,
      body: form.body,
      from: form.from
    }

    if (activePreset.processLoadQuotes) payload.processLoadQuotes = activePreset.processLoadQuotes
    if (activePreset.errorProcessToLoad)
      payload.errorProcessToLoad = activePreset.errorProcessToLoad
    if (activePreset.selfManagedDataCollection)
      payload.selfManagedDataCollection = activePreset.selfManagedDataCollection
    if (activePreset.dataQuestionUnit) payload.dataQuestionUnit = activePreset.dataQuestionUnit
    if (activePreset.unitCallConversationExist)
      payload.unitCallConversationExist = activePreset.unitCallConversationExist
    if (activePreset.escludeMidOfficeFlow)
      payload.escludeMidOfficeFlow = activePreset.escludeMidOfficeFlow
    if (activePreset.goelettRoleEmails) payload.goelettRoleEmails = activePreset.goelettRoleEmails

    setMessages((prev) => [...prev, { user: form.from, message: form.body, createdAt: new Date() }])
    setIsLoading(true)
    setStatus(null)

    try {
      const res = await FC.service('public').create(payload)
      setStatus('success')
      if (!currentAIC) setCurrentAIC(aic)
      if (
        res?.conversationData?.conversationId &&
        res.conversationData.conversationId !== conversationId
      ) {
        setConversationId(res.conversationData.conversationId)
      }
      dispatch({ type: 'SET_FIELD', field: 'body', value: '' })
    } catch (error) {
      setStatus('error')
      showToast('error', t.errore || 'Errore', error?.message || t.dgSendError)
    } finally {
      setIsLoading(false)
    }
  }, [form, userId, env, conversationId, activePreset, currentAIC])

  const handleNewConversation = useCallback(() => {
    dispatch({ type: 'RESET', preset: activePreset })
    setMessages([])
    setConversationId(undefined)
    setCurrentAIC(null)
    setStatus(null)
  }, [activePreset])

  const statusClass = isLoading ? 'loading' : status
  const statusLabel = isLoading
    ? t.dgStatusLoading
    : status === 'success'
      ? t.dgStatusSuccess
      : status === 'error'
        ? t.dgStatusError
        : t.dgStatusWaiting

  if (!isSuccess) {
    return (
      <div className="demo-goelett" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <i className="pi pi-spin pi-spinner" style={{ fontSize: 32, color: '#6366f1' }} />
      </div>
    )
  }

  return (
    <div className="demo-goelett">
      <div className="demo-goelett__header">
        <div className="demo-goelett__header-icon">
          <i className="pi pi-send" />
        </div>
        <div className="demo-goelett__header-text">
          <h1>{t.dgTitle}</h1>
          <p>{t.dgSubtitle}</p>
        </div>
        <div className="demo-goelett__preset-selector">
          <label>{t.dgPresetLabel}</label>
          <select
            value={activePresetKey}
            onChange={(e) => handlePresetChange(e.target.value)}
            disabled={isLoading}
          >
            <optgroup label={t.dgPresetGroupClient || 'Configurazioni client'}>
              {(isOperatorGoelett
                ? CLIENT_CONFIGS.filter(({ id }) => id === 'goelett_test')
                : CLIENT_CONFIGS
              ).map(({ id, label, description }) => (
                <option key={id} value={id}>
                  {label} — {description}
                </option>
              ))}
            </optgroup>
            {!isOperatorGoelett && Object.keys(savedPresets).length > 0 && (
              <optgroup label={t.dgPresetGroupSaved || 'Preset salvati'}>
                {Object.keys(savedPresets).map((name) => (
                  <option key={`${CUSTOM_PREFIX}${name}`} value={`${CUSTOM_PREFIX}${name}`}>
                    {name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>
      </div>

      <div className="demo-goelett__body">
        <div className="demo-goelett__email-panel">
          <div className="demo-goelett__email-header">
            <i className="pi pi-envelope" />
            {t.dgEmailPanel}
            <span className="demo-goelett__preset-badge">
              <i className="pi pi-check-circle" />
              {activePreset.label}
            </span>
          </div>

          <div className="demo-goelett__email-form">
            <div className="demo-goelett__field">
              <label>{t.dgFrom}</label>
              <input
                type="text"
                value={form.from}
                onChange={(e) => setField('from', e.target.value)}
                placeholder={t.dgFromPlaceholder}
              />
            </div>

            <div className="demo-goelett__field">
              <label>{t.dgSubject}</label>
              <input
                type="text"
                value={form.subject}
                onChange={(e) => setField('subject', e.target.value)}
                placeholder={t.dgSubjectPlaceholder}
              />
            </div>

            <div className="demo-goelett__field">
              <label>{t.dgBody}</label>
              <textarea
                value={form.body}
                onChange={(e) => setField('body', e.target.value)}
                placeholder={t.dgBodyPlaceholder}
              />
            </div>
          </div>

          <div className="demo-goelett__send-bar">
            <button
              className="demo-goelett__send-btn"
              onClick={handleSend}
              disabled={isLoading || !form.body?.trim()}
            >
              {isLoading ? (
                <>
                  <i className="pi pi-spin pi-spinner" /> {t.dgSending}
                </>
              ) : (
                <>
                  <i className="pi pi-send" />{' '}
                  {conversationId ? t.dgContinue || 'Rispondi' : t.dgSend}
                </>
              )}
            </button>
            <button
              className="demo-goelett__send-btn demo-goelett__send-btn--reset"
              onClick={handleNewConversation}
              disabled={isLoading}
              title={t.dgNewConversation || 'Nuova conversazione'}
            >
              <i className="pi pi-plus" />
              {t.dgNewConversation || 'Nuova conversazione'}
            </button>
          </div>
        </div>

        <div className="demo-goelett__chat-panel">
          <div className="demo-goelett__chat-header">
            <div className="demo-goelett__chat-header-left">
              <i className="pi pi-comments" />
              {t.dgConversation}
              {conversationId && (
                <span style={{ fontWeight: 400, fontSize: 11, color: 'var(--ink-500)' }}>
                  · ID: {conversationId}
                </span>
              )}
            </div>
            {conversationId && (
              <button
                type="button"
                className="demo-goelett__open-conv-btn"
                onClick={() => window.open(`/operatore/${conversationId}`, '_blank', 'noopener')}
              >
                <i className="pi pi-external-link" />
                {t.dgOpenConversation}
              </button>
            )}
            <span className={`demo-goelett__status demo-goelett__status--${statusClass || ''}`}>
              <i
                className={`pi ${isLoading ? 'pi-spin pi-spinner' : status === 'success' ? 'pi-check-circle' : status === 'error' ? 'pi-times-circle' : 'pi-circle'}`}
                style={{ fontSize: 10 }}
              />
              {statusLabel}
            </span>
          </div>

          <div className="demo-goelett__chat-body">
            {messages.length === 0 ? (
              <div className="demo-goelett__chat-empty">
                <i className="pi pi-inbox" />
                <span>{t.dgEmpty}</span>
              </div>
            ) : (
              <>
                <GigiChatMessages messages={messages} isReadOnly={false} />
                <div ref={chatEndRef} />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
